import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { LazyIcon } from "../../components/LazyIcon";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { useTowingEquipmentState } from "../../containers/TowingEquipmentContainer";
import { useUserPreferences } from "../../containers/UserPreferencesContainer";
import { Hangar, Ramp } from "../../types";

type Props = {
  locationType: string;
  isScheduledView?: boolean;
  location?: Hangar | Ramp;
  onLocationUpdate?: () => void;
};

export const ViewSettings: React.FC<Props> = ({
  locationType,
  isScheduledView = false,
  location,
  onLocationUpdate,
}) => {
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const { towingEquipments } = useTowingEquipmentState();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { preferences, setPreferences } = useUserPreferences();

  const isAutoParkEnabled = activeFBO?.settings?.enableAutoParking ?? false;

  const showCoPilot =
    locationType === "hangar" &&
    activeFBO?.subscription.includes("autostacking");

  const handleTugChange = async (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const newTugId = evt.target.value || null;

    if (!location) {
      snackbar.notify({
        text: "Location not available",
        severity: "error",
      });
      return;
    }

    try {
      if (locationType === "hangar") {
        await postgrest
          .from("hangar")
          .update({ towing_equipment_id: newTugId })
          .eq("id", location.id);
      } else if (locationType === "ramp") {
        await postgrest
          .from("ramp")
          .update({ towing_equipment_id: newTugId })
          .eq("id", location.id);
      }

      snackbar.notify({
        text: `Tug updated for ${location.name}`,
        severity: "success",
      });
      onLocationUpdate?.();
    } catch (error) {
      console.error("Failed to update tug:", error);
      snackbar.notify({
        text: "Failed to update tug",
        severity: "error",
      });
    }
  };

  const fboDefaultTug = towingEquipments?.find((t) => t.is_default);

  return (
    <>
      <Button
        variant="contained"
        color="info"
        onClick={handleClick}
        startIcon={<LazyIcon name="TuneIcon" />}
        sx={{
          "@media (max-width: 1300px)": { display: "none" },
        }}
      >
        View Settings
      </Button>
      <Button
        color="info"
        variant="contained"
        onClick={handleClick}
        sx={{
          "@media (min-width: 1300px)": { display: "none" },
          mr: 1,
        }}
      >
        <LazyIcon name="TuneIcon" />
      </Button>
      <Popover
        data-testid="view_settings"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack direction="column" alignItems="flex-end" sx={{ p: 2 }}>
          <Tooltip title="Display tail number on aircraft" placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_tail_number"
                  color="primary"
                  checked={preferences.forceDisplayTailNumber ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      forceDisplayTailNumber:
                        !preferences.forceDisplayTailNumber,
                    })
                  }
                />
              }
              label="Tail #"
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip
            title="To see single note vs all notes, go to Settings > Configuration"
            placement="right"
          >
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={preferences.notes ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      notes: !preferences.notes,
                    })
                  }
                />
              }
              label="Notes"
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip title="Display aircraft model on aircraft" placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_model"
                  color="primary"
                  checked={preferences.forceDisplayModel ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      forceDisplayModel: !preferences.forceDisplayModel,
                    })
                  }
                />
              }
              label="Model"
              labelPlacement="start"
            />
          </Tooltip>
          <Tooltip title="Horizontal spacing outline" placement="right">
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_sop_spacing"
                  color="primary"
                  checked={preferences.showSOPSpacing ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      showSOPSpacing: !preferences.showSOPSpacing,
                    })
                  }
                />
              }
              label="SOP Spacing"
              labelPlacement="start"
            />
          </Tooltip>
          {locationType === "hangar" && (
            <>
              <Tooltip title="Wall spacing visual fill areas" placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_wall_spacing_fill"
                      color="primary"
                      checked={preferences.showWallSpacingFill ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showWallSpacingFill: !preferences.showWallSpacingFill,
                        })
                      }
                    />
                  }
                  label="Wall Spacing Fill"
                  labelPlacement="start"
                />
              </Tooltip>
              <Tooltip
                title="Show bay boundaries for stepped-eave hangars"
                placement="right"
              >
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_bay_boundaries"
                      color="primary"
                      checked={preferences.showBayBoundaries ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showBayBoundaries: !preferences.showBayBoundaries,
                        })
                      }
                    />
                  }
                  label="Bay Boundaries"
                  labelPlacement="start"
                />
              </Tooltip>
            </>
          )}
          <Tooltip
            title="Highlights all overlaps, doesn't evaluate vertical spacing safety"
            placement="right"
          >
            <FormControlLabel
              control={
                <Switch
                  data-testid="toggle_overlap"
                  color="primary"
                  checked={preferences.showOverlaps ?? false}
                  onChange={() =>
                    setPreferences({
                      ...preferences,
                      showOverlaps: !preferences.showOverlaps,
                    })
                  }
                />
              }
              label="Overlaps"
              labelPlacement="start"
            />
          </Tooltip>
          {showCoPilot && (
            <Tooltip
              title="Select unstacked aircraft and get SOP-compliant options"
              placement="right"
            >
              <FormControlLabel
                disabled={!activeFBO?.subscription.includes("autostacking")}
                control={
                  <Switch
                    data-testid="toggle_copilot"
                    color="primary"
                    checked={preferences.enableCoPilot ?? false}
                    onChange={() =>
                      setPreferences({
                        ...preferences,
                        enableCoPilot: !preferences.enableCoPilot,
                      })
                    }
                  />
                }
                label="Co-Pilot"
                labelPlacement="start"
              />
            </Tooltip>
          )}
          {locationType === "hangar" &&
            location &&
            towingEquipments &&
            towingEquipments.length > 0 && (
              <TextField
                select
                label="Tug"
                value={location.towing_equipment_id || ""}
                onChange={handleTugChange}
                size="small"
                sx={{ minWidth: 200 }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (!value) {
                      return `Use FBO Default (${fboDefaultTug?.name || "None"})`;
                    }
                    const tug = towingEquipments.find(t => t.towing_equipment_id === value);
                    return tug?.name || value;
                  }
                }}
              >
                <MenuItem value="">
                  Use FBO Default ({fboDefaultTug?.name || "None"})
                </MenuItem>
                {towingEquipments
                  .filter((t) => t.in_use)
                  .map((tug) => (
                    <MenuItem
                      key={tug.towing_equipment_id}
                      value={tug.towing_equipment_id}
                    >
                      {tug.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          {locationType === "ramp" && (
            <>
              <Tooltip title="Display parking spots on ramp" placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_spots"
                      color="primary"
                      checked={preferences.showSpots ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showSpots: !preferences.showSpots,
                        })
                      }
                    />
                  }
                  label="Spots"
                  labelPlacement="start"
                />
              </Tooltip>
              <Tooltip title="Overlay ramp satellite image" placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="toggle_ramp_reference_image"
                      color="primary"
                      checked={preferences.showRampReferenceImage ?? false}
                      onChange={() =>
                        setPreferences({
                          ...preferences,
                          showRampReferenceImage:
                            !preferences.showRampReferenceImage,
                        })
                      }
                    />
                  }
                  label="Ramp Image"
                  labelPlacement="start"
                />
              </Tooltip>
              {isScheduledView && isAutoParkEnabled && (
                <>
                  <Tooltip
                    title={`Show zone occupancy heatmap (next ${
                      preferences.heatmapHorizonHours || 72
                    }h)`}
                    placement="right"
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          data-testid="toggle_zone_heatmap"
                          color="primary"
                          checked={preferences.showZoneHeatmap ?? false}
                          onChange={() =>
                            setPreferences({
                              ...preferences,
                              showZoneHeatmap: !preferences.showZoneHeatmap,
                            })
                          }
                        />
                      }
                      label="Zone Heatmap"
                      labelPlacement="start"
                    />
                  </Tooltip>
                  {preferences.showZoneHeatmap && (
                    <Box sx={{ pl: 7, pb: 1 }}>
                      <ToggleButtonGroup
                        size="small"
                        value={preferences.heatmapHorizonHours || 72}
                        exclusive
                        onChange={(e, value) => {
                          if (value !== null) {
                            setPreferences({
                              ...preferences,
                              heatmapHorizonHours: value,
                            });
                          }
                        }}
                        sx={{ height: 28 }}
                      >
                        <ToggleButton value={24}>24h</ToggleButton>
                        <ToggleButton value={72}>72h</ToggleButton>
                        <ToggleButton value={168}>1w</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                </>
              )}
              {location && towingEquipments && towingEquipments.length > 0 && (
                <TextField
                  select
                  label="Tug"
                  value={location.towing_equipment_id || ""}
                  onChange={handleTugChange}
                  size="small"
                  sx={{ minWidth: 200 }}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) => {
                      if (!value) {
                        return `Use FBO Default (${fboDefaultTug?.name || "None"})`;
                      }
                      const tug = towingEquipments.find(t => t.towing_equipment_id === value);
                      return tug?.name || value;
                    }
                  }}
                >
                  <MenuItem value="">
                    Use FBO Default ({fboDefaultTug?.name || "None"})
                  </MenuItem>
                  {towingEquipments
                    .filter((t) => t.in_use)
                    .map((tug) => (
                      <MenuItem
                        key={tug.towing_equipment_id}
                        value={tug.towing_equipment_id}
                      >
                        {tug.name}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            </>
          )}
        </Stack>
      </Popover>
    </>
  );
};
